import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, AppWindow, Building2, Users } from 'lucide-react';

import { useAnalytics } from '@/store/analytics';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';

const cardConfig = [
    {
        key: 'tenants',
        title: 'Active Tenants',
        subtitle: 'Organizations currently enabled on the platform.',
        icon: Building2,
        to: '/m/tenants',
    },
    {
        key: 'applications',
        title: 'Integrated Applications',
        subtitle: 'Registered applications and enabled integrations.',
        icon: AppWindow,
        to: '/m/applications',
    },
    {
        key: 'monthly_active_users',
        title: 'Monthly Active Users',
        subtitle: 'Distinct actors with successful auth activity in the last 30 days.',
        icon: Users,
        to: '/m/analytics',
    },
    {
        key: 'system_uptime',
        title: 'System Uptime',
        subtitle: 'Average uptime reported by application health checks.',
        icon: Activity,
        to: '/m/analytics',
    },
] as const;

export const Dashboard = () => {
    const navigate = useNavigate();
    const { setBreadcrumbs } = useLayout();
    const { summary, isLoading, fetchSummary, fetchTenantAnalytics, tenant } = useAnalytics();

    useEffect(() => {
        setBreadcrumbs([{ name: 'Dashboard' }]);
        void fetchSummary();
        void fetchTenantAnalytics(30);
    }, [fetchSummary, fetchTenantAnalytics, setBreadcrumbs]);

    if (isLoading && !summary) {
        return <Loader />;
    }

    const cards = cardConfig.map((card) => {
        const value = summary?.metrics?.[card.key] ?? 0;
        return {
            ...card,
            value: card.key === 'system_uptime' ? `${Number(value).toFixed(2)}%` : `${value}`,
        };
    });

    return (
        <div className="mx-auto flex max-w-7xl flex-col gap-8 p-4 lg:p-8">
            <div className="space-y-2">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-400">Overview</p>
                <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Platform dashboard</h1>
                <p className="max-w-3xl text-sm text-slate-500 dark:text-slate-400">
                    Live platform metrics sourced from the analytics API. Use the cards below to jump directly into the operational views.
                </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {cards.map((card) => (
                    <button
                        key={card.key}
                        onClick={() => navigate(card.to)}
                        className="rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-950 dark:hover:border-orange-500/30"
                    >
                        <div className="mb-6 flex items-center justify-between">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-orange-500 dark:bg-orange-500/10">
                                <card.icon className="h-5 w-5" />
                            </div>
                            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Live</span>
                        </div>
                        <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{card.title}</p>
                        <p className="mt-2 text-4xl font-bold text-slate-900 dark:text-white">{card.value}</p>
                        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">{card.subtitle}</p>
                    </button>
                ))}
            </div>

            <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                    <div className="mb-5 flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Recent authentication activity</h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Last 30-day tenant authentication trend.</p>
                        </div>
                        <button
                            onClick={() => navigate('/m/analytics')}
                            className="text-sm font-semibold text-orange-500 transition hover:text-orange-600"
                        >
                            View analytics
                        </button>
                    </div>
                    <div className="space-y-3">
                        {(tenant?.auth_time_series || []).slice(-8).map((point) => (
                            <div key={point.day} className="grid grid-cols-[120px_1fr_1fr] items-center gap-4 rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-900/60">
                                <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{point.day}</span>
                                <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Successes</p>
                                    <p className="text-lg font-semibold text-slate-900 dark:text-white">{point.successes}</p>
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Failures</p>
                                    <p className="text-lg font-semibold text-slate-900 dark:text-white">{point.failures}</p>
                                </div>
                            </div>
                        ))}
                        {!tenant?.auth_time_series?.length && (
                            <p className="rounded-2xl bg-slate-50 px-4 py-6 text-sm text-slate-500 dark:bg-slate-900/60 dark:text-slate-400">
                                No authentication activity available yet.
                            </p>
                        )}
                    </div>
                </section>

                <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                    <div className="mb-5">
                        <h2 className="text-lg font-bold text-slate-900 dark:text-white">Top active users</h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Most active actors across recent sign-in traffic.</p>
                    </div>
                    <div className="space-y-3">
                        {(tenant?.top_active_users || []).slice(0, 6).map((item) => (
                            <div key={item.actor} className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-900/60">
                                <div>
                                    <p className="font-medium text-slate-900 dark:text-white">{item.actor}</p>
                                    <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Logins</p>
                                </div>
                                <span className="text-xl font-bold text-orange-500">{item.login_count}</span>
                            </div>
                        ))}
                        {!tenant?.top_active_users?.length && (
                            <p className="rounded-2xl bg-slate-50 px-4 py-6 text-sm text-slate-500 dark:bg-slate-900/60 dark:text-slate-400">
                                No user activity available yet.
                            </p>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};
